import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import ErpTemplate from '../../components/ErpTemplate';
import { FaClipboardCheck, FaSyncAlt, FaTimes, FaCheck, FaBan, FaDownload } from 'react-icons/fa';
import FormButton from '../../components/FormButton';
import TabelaLoading from '../../components/TabelaLoading';
import api from '../../services/api';
import Alert from '../../components/Alert';
import { FinField, FinInput, FinTextarea } from '../../components/FinField';
import {
    PRAZOS_XML, TIPOS_CONTA, TIPOS_PIX, rotuloDe, rotuloContato,
    TIPOS_FORNECEDOR, DOCUMENTOS_FISCAIS, REGIMES_IRPJ, REGIMES_IBS_CBS,
    DESTAQUE_DOCUMENTOS, TRIBUTOS_RETENCAO, TIPOS_DOCUMENTO,
    NATUREZAS_CADASTRO, REGIMES_ESPECIFICOS, SITUACOES_CADASTRO, CLASSIFICACOES_CREDITO,
    PERIODICIDADES_REVISAO, SIM_NAO, opcoesDe, placeholderDe, INDICADORES_INSCRICAO, rotuloDoSemestre,
    CAMINHOS_SUBMISSAO,
} from '../../util/fornecedorCadastro';
import ReactSelect from '../../components/ReactSelect';
import '../../css/financeiro-parcela-pagar.css';

// `novos` não é status: é a filial que o link de uma rede cadastrou sem achar a sua na
// lista. Chega sem fornecedor, só com a rede, e espera revisão numa faixa própria.
const FAIXA_NOVOS = 'novos';

const FAIXAS = [
    { valor: 'recebida', rotulo: 'Aguardando revisão' },
    { valor: FAIXA_NOVOS, rotulo: 'Novos' },
    { valor: 'aplicada', rotulo: 'Aplicadas' },
    { valor: 'rejeitada', rotulo: 'Rejeitadas' },
];

const PILL = { recebida: 'is-warn', aplicada: 'is-ok', rejeitada: 'is-danger' };
const ROTULO = { recebida: 'Aguardando', aplicada: 'Aplicada', rejeitada: 'Rejeitada' };

const dataBr = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '—');

/**
 * Fila de revisão do formulário preenchido pelo fornecedor.
 *
 * Dado que chega por link público não vira cadastro sozinho — passa por aqui. O
 * revisor compara lado a lado o que está no cadastro e o que o fornecedor
 * declarou, e só então aplica.
 */
export default function PainelSubmissaoFornecedor() {
    const token = localStorage.getItem('token');

    const [faixa, setFaixa] = useState('recebida');
    const [submissoes, setSubmissoes] = useState([]);
    const [contadores, setContadores] = useState({});
    const [carregando, setCarregando] = useState(true);

    const [detalhe, setDetalhe] = useState(null);
    const [motivo, setMotivo] = useState('');

    // Nota do revisor sobre ESTA revisão — o "por que aceitei" que a área pede no
    // histórico. Opcional: exigir justificativa em toda aprovação faria o revisor
    // escrever "ok" quinhentas vezes, que é ruído com aparência de rastro.
    const [observacao, setObservacao] = useState('');

    // Seção 11 — o que o revisor está editando. Separado de `detalhe.controle`
    // porque só sobe no salvar: fechar o modal sem salvar não pode ter mexido em
    // nada, e um estado compartilhado daria a impressão contrária.
    const [controleEdit, setControleEdit] = useState({});
    const [salvandoControle, setSalvandoControle] = useState(false);
    const [salvando, setSalvando] = useState(false);

    // "Aplicar em": outro cadastro, escolhido pelo revisor, recebe a submissão como atualização —
    // um dos repetidos de antes da unificação, ou o cadastro com CNPJ fora do formato que o
    // formulário não achou. Nulo é o padrão: o cadastro que o CNPJ resolveu, ou o novo.
    const [aplicarEm, setAplicarEm] = useState(null);

    const buscar = useCallback(() => {
        setCarregando(true);
        api({
            method: 'get',
            action: '/fornecedor/submissao',
            token,
            params: {
                ...(faixa === FAIXA_NOVOS ? { status: 'recebida', novos: 1 } : { status: faixa }),
            },
        })
            .then(response => {
                const d = response.data?.data ?? {};
                const lista = d.submissoes ?? [];
                // Cadastro novo e troca de CNPJ são as submissões sem fornecedor. Filtrar aqui
                // também mantém a faixa certa mesmo num servidor que ainda não entende `novos`.
                setSubmissoes(faixa === FAIXA_NOVOS ? lista.filter(s => !s.fornecedor) : lista);
                setContadores(d.contadores ?? {});
            })
            .catch(() => { })
            .finally(() => setCarregando(false));
        // eslint-disable-next-line
    }, [faixa]);

    useEffect(() => { buscar(); }, [buscar]);

    /**
     * As linhas da tabela das seções 4 a 7.
     *
     * `exibir` existe porque três desses campos não são texto: dois são código
     * (rótulo humano vem da lista) e um é array. Renderizar o valor cru mostraria
     * `simples_regime_regular` e `["nfe","nfse"]` para quem precisa julgar se está
     * certo.
     */
    const LINHAS_FISCAIS = [
        { campo: 'tipo_fornecedor', rotulo: 'Tipo de fornecedor', exibir: v => rotuloDe(TIPOS_FORNECEDOR, v) },
        { campo: 'documentos_fiscais', rotulo: 'Documentos emitidos', exibir: v => (v || []).map(x => rotuloDe(DOCUMENTOS_FISCAIS, x)).join(', ') },
        { campo: 'itens_fornecidos', rotulo: 'Itens fornecidos', exibir: v => v },
        { campo: 'ncm', rotulo: 'NCM principais', exibir: v => v },
        { campo: 'nbs', rotulo: 'NBS principal', exibir: v => v },
        { campo: 'locais_execucao', rotulo: 'Municípios / UFs', exibir: v => v },
        // A seção 5.1 não aparecia: só o fundamento estava aqui, e ele só existe
        // quando o regime é imune/isento. O revisor aprovava sem enxergar o regime
        // que o fornecedor declarou.
        { campo: 'regime_irpj', rotulo: 'Regime IRPJ/CSLL', exibir: v => rotuloDe(REGIMES_IRPJ, v) },
        { campo: 'regime_irpj_fundamento', rotulo: 'Fundamento da imunidade', exibir: v => v },
        { campo: 'regime_ibs_cbs', rotulo: 'Regime IBS/CBS', exibir: v => rotuloDe(REGIMES_IBS_CBS, v) },
        { campo: 'vigencia_opcao', rotulo: 'Vigência da opção (semestre/ano)', exibir: v => rotuloDoSemestre(v) },
        { campo: 'data_opcao_simples', rotulo: 'Data da opção no Portal', exibir: v => dataBr(v) },
        { campo: 'opcao_proxima_limite', rotulo: 'Limite da próxima opção', exibir: v => dataBr(v) },
        { campo: 'regime_especifico', rotulo: 'Regime específico', exibir: v => rotuloDe(REGIMES_ESPECIFICOS, v) },
        { campo: 'regime_especifico_outro', rotulo: 'Qual regime', exibir: v => v },
        { campo: 'reducao_percentual', rotulo: 'Redução, alíquota zero, isenção ou imunidade', exibir: v => v },
        { campo: 'contribuinte_iss', rotulo: 'Contribuinte de ISS', exibir: v => rotuloDe(SIM_NAO, v) },
        { campo: 'destaque_documentos', rotulo: 'Destaque de CBS/IBS', exibir: v => rotuloDe(DESTAQUE_DOCUMENTOS, v) },
        { campo: 'cst', rotulo: 'CST', exibir: v => v },
        { campo: 'cclasstrib', rotulo: 'cClassTrib', exibir: v => v },
        { campo: 'credito_presumido_aplica', rotulo: 'Aplica crédito presumido', exibir: v => rotuloDe(SIM_NAO, v) },
        { campo: 'diferimento_aplica', rotulo: 'Opera com diferimento', exibir: v => rotuloDe(SIM_NAO, v) },
        { campo: 'diferimento_suspensao', rotulo: 'Hipótese do diferimento', exibir: v => v },
        // Seção 7: o campo livre sobre a transição e a data dela. São declaração do
        // fornecedor e estavam sendo gravadas sem passar pelos olhos de ninguém — o
        // revisor aprovava um texto que a tela dele não mostrava.
        { campo: 'data_transicao_iva', rotulo: 'Data da transição do IVA', exibir: v => dataBr(v) },
        { campo: 'retencoes_outras', rotulo: 'Outras retenções', exibir: v => v },
        { campo: 'observacao_fiscal', rotulo: 'Observações sobre retenções', exibir: v => v },
    ];

    // O detalhe vem com o lado "No cadastro" de quem vai receber a submissão: o cadastro dela, ou
    // o escolhido no "Aplicar em".
    const carregarDetalhe = (id, destino) => api({
        method: 'get',
        action: `/fornecedor/submissao/${id}`,
        token,
        params: destino ? { aplicar_em: destino.value } : {},
    });

    // A mesma busca de fornecedor do orçamento e do cadastro de grupo: nome, código, fantasia ou CNPJ.
    const buscarFornecedores = (texto) => api({
        method: 'get',
        action: '/participante',
        token,
        params: { nome: texto, tipoCadastro: ['orcamento'], limit: 50 },
    })
        .then(response => (response.data?.data ?? []).map(registro => ({
            value: registro.id,
            label: (registro.codigo ?? '') + ' - ' + registro.nome + (registro.cnpj ? ' (' + registro.cnpj + ')' : ''),
        })))
        .catch(() => []);

    // Trocar o destino recarrega o comparativo com ele; limpar volta ao padrão.
    const escolherDestino = (opcao) => {
        const id = detalhe?.submissao?.id;
        if (!id) return;
        setAplicarEm(opcao);
        setDetalhe(d => ({ ...d, carregando: true }));
        carregarDetalhe(id, opcao)
            .then(response => setDetalhe({ carregando: false, ...(response.data?.data ?? {}) }))
            .catch(erro => {
                setAplicarEm(null);
                setDetalhe(d => ({ ...d, carregando: false }));
                Alert({
                    icon: 'error',
                    title: 'Não foi possível carregar o cadastro escolhido',
                    html: erro?.response?.data?.error || 'Tente novamente em instantes.',
                });
            });
    };

    const abrir = (id) => {
        setDetalhe({ carregando: true });
        setAplicarEm(null);
        setMotivo('');
        setObservacao('');
        setControleEdit({});
        carregarDetalhe(id, null)
            .then(response => setDetalhe({ carregando: false, ...(response.data?.data ?? {}) }))
            // Falha aqui fechava o modal em silêncio, e o revisor não sabia se tinha
            // errado o clique ou se a API caiu.
            .catch(erro => {
                setDetalhe(null);
                Alert({
                    icon: 'error',
                    title: 'Não foi possível abrir a submissão',
                    // `html`, não `text`: o Alert do projeto não tem parâmetro `text`
                    // e o descartaria em silêncio — a causa real ficaria invisível.
                    html: erro?.response?.data?.error || 'Tente novamente em instantes.',
                });
            });
    };

    // Vindo da aba Formulário do cadastro do fornecedor (`?submissao=<id>`): abre a dele direto.
    const location = useLocation();
    useEffect(() => {
        const idSubmissao = new URLSearchParams(location.search).get('submissao');
        if (idSubmissao) abrir(idSubmissao);
        // eslint-disable-next-line
    }, [location.search]);

    const revisar = (acao) => {
        const id = detalhe?.submissao?.id;
        if (!id) return;

        setSalvando(true);
        api({
            method: 'post',
            action: `/fornecedor/submissao/${id}/${acao}`,
            token,
            data: acao === 'rejeitar'
                ? { motivo }
                : { observacao, ...(aplicarEm ? { aplicar_em: aplicarEm.value } : {}) },
        })
            .then(() => {
                setDetalhe(null);
                buscar();
            })
            .catch(error => {
                Alert({ icon: 'error', title: error.response?.data?.error || 'Não foi possível concluir a revisão' });
            })
            .finally(() => setSalvando(false));
    };

    /**
     * Linhas de comparação. Só monta o que existe dos dois lados — campo que o
     * fornecedor não preencheu não vira linha, senão a tela sugere que ele apagou
     * algo que apenas não respondeu.
     */
    // Os dois lados dos contatos. O backend já devolve `atual.contatos` no show();
    // a tela é que ignorava, escondendo o que a aprovação vai substituir.
    const contatosAtuais = Array.isArray(detalhe?.atual?.contatos) ? detalhe.atual.contatos : [];
    const contatosDeclarados = Array.isArray(detalhe?.submissao?.payload?.contatos)
        ? detalhe.submissao.payload.contatos
        : [];

    // Seções 4 a 7. `fiscalAtual` pode ser nulo — fornecedor que nunca preencheu o
    // formulário não tem perfil fiscal, e a coluna "No cadastro" mostra travessão em
    // vez de fingir que havia algo.
    const fiscalDeclarado = detalhe?.submissao?.payload?.fiscal || null;
    const fiscalAtual = detalhe?.atual?.fiscal || null;

    // Cadastro novo ou troca de CNPJ: não há cadastro para comparar, e aplicar CRIA o fornecedor.
    // Na troca, aplicar também inativa o cadastro do CNPJ antigo.
    const ehNovo = !!detalhe?.submissao && !detalhe.submissao.fornecedor && !aplicarEm;
    const substituido = detalhe?.submissao?.substituido_data || null;

    // Só o que ele marcou como incidente. Tributo desmarcado não vira pílula: a tela
    // responde "o que incide", e listar os oito com "não" afogaria a resposta.
    const retencoesDeclaradas = TRIBUTOS_RETENCAO
        .filter(t => fiscalDeclarado?.retencoes?.[t.valor]?.aplica)
        .map(t => ({
            ...t,
            aliquota: fiscalDeclarado.retencoes[t.valor].aliquota,
            observacao: fiscalDeclarado.retencoes[t.valor].observacao,
        }));

    // Regra do anexo: o favorecido deve coincidir com o emitente da nota. Aviso, não
    // trava — é o revisor quem devolve para regularização, e há caso legítimo (conta
    // de matriz para filial emitente) que precisa poder ser declarado.
    const soDigitos = (v) => String(v || '').replace(/\D/g, '');
    const favorecidoDiverge = Boolean(
        detalhe?.submissao?.payload?.banco?.cnpj_favorecido
        && soDigitos(detalhe.submissao.payload.banco.cnpj_favorecido)
            !== soDigitos(detalhe.submissao.payload.cnpj)
    );

    /**
     * Salva a seção 11. Só o que o revisor tocou viaja — o back trata campo ausente
     * como "não mexer", então mandar o objeto inteiro apagaria o que ele não abriu.
     */
    const salvarControle = () => {
        if (!detalhe?.submissao?.fornecedor) return;

        setSalvandoControle(true);

        api({
            method: 'put',
            action: `/fornecedor/classificacao/${detalhe.submissao.fornecedor}`,
            token,
            data: controleEdit,
        })
            .then(resposta => {
                setDetalhe(d => d && ({ ...d, atual: { ...d.atual, fiscal: resposta.data?.data ?? d.atual?.fiscal } }));
                setControleEdit({});
                Alert({ icon: 'success', title: 'Classificação atualizada' });
            })
            .catch(erro => Alert({
                icon: 'error',
                title: erro.response?.data?.error || 'Não foi possível salvar a classificação',
            }))
            .finally(() => setSalvandoControle(false));
    };

    const comparacao = () => {
        if (!detalhe?.submissao) return [];

        const p = detalhe.submissao.payload || {};
        const a = detalhe.atual || {};
        const pe = p.endereco || {};
        const ae = a.endereco || {};

        const linhas = [
            ['Razão social', a.razaosocial, p.razaosocial],
            ['Nome fantasia', a.nomefantasia, p.nomefantasia],
            ['CNPJ', a.cnpj, p.cnpj],
            ['Inscrição estadual', a.inscricaoestadual, p.inscricaoestadual],
            ['Inscrição municipal', a.inscricaomunicipal, p.inscricaomunicipal],
            ['Contribuinte de ICMS', rotuloDe(INDICADORES_INSCRICAO, a.indicadorinscricao), rotuloDe(INDICADORES_INSCRICAO, p.indicadorinscricao)],
            ['CEP', ae.cep, pe.cep],
            ['Logradouro', ae.logradouro, pe.logradouro],
            ['Número', ae.numero, pe.numero],
            ['Complemento', ae.complemento, pe.complemento],
            ['Bairro', ae.bairro, pe.bairro],
            ['UF', ae.uf, pe.uf],
            ['Município', ae.municipio, pe.municipio?.nome],
            ['E-mail para XML', a.email_xml, p.email_xml],
            // Rótulo, não código: o revisor lia `ate_24h` e `cbs_ibs` crus. O mapa é
            // o MESMO que o formulário público usa para oferecer as opções.
            ['Prazo de envio do XML', rotuloDe(PRAZOS_XML, a.prazo_envio_xml), rotuloDe(PRAZOS_XML, p.prazo_envio_xml)],
            // FK antes do snapshot, igual ao formulário público e à tela do operador:
            // conta do backfill tem banco pela relação e snapshot nulo.
            ['Banco', a.banco?.banco_data?.nome ?? a.banco?.nome_banco_snapshot, p.banco?.nome_banco],
            ['Agência', a.banco?.agencia, p.banco?.agencia],
            ['Conta', a.banco?.conta, p.banco?.conta],
            ['Tipo de conta', rotuloDe(TIPOS_CONTA, a.banco?.tipo_conta), rotuloDe(TIPOS_CONTA, p.banco?.tipo_conta)],
            ['Tipo de chave PIX', rotuloDe(TIPOS_PIX, a.banco?.pix_tipo), rotuloDe(TIPOS_PIX, p.banco?.pix_tipo)],
            ['Chave PIX', a.banco?.pix_chave, p.banco?.pix_chave],
            ['Favorecido', a.banco?.titular, p.banco?.titular],
            ['CNPJ do favorecido', a.banco?.cnpj_favorecido, p.banco?.cnpj_favorecido],
        ];

        return linhas
            .filter(([, atual, declarado]) => declarado || atual)
            .map(([rotulo, atual, declarado]) => {
                const temAtual = String(atual ?? '') !== '';
                const temDeclarado = String(declarado ?? '') !== '';

                return {
                    rotulo,
                    atual: temAtual ? atual : '—',
                    declarado: temDeclarado ? declarado : '—',
                    mudou: temDeclarado && String(declarado) !== String(atual ?? ''),
                    // Campo que EXISTE no cadastro e veio em branco. Antes isto era
                    // marcado como "não mudou" — o revisor aprovava um apagamento sem
                    // enxergá-lo. Hoje a aplicação preserva o valor, e o rótulo diz
                    // isso em vez de calar.
                    vazio: temAtual && !temDeclarado,
                };
            });
    };

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">Revisão de Cadastro</h1>
                        <p className="fin-pp__subtitle">
                            Formulários preenchidos pelos fornecedores. Nada entra no cadastro sem passar por aqui.
                        </p>
                    </div>
                    <div className="fin-pp__actions">
                        <button type="button" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm" onClick={buscar}>
                            <FaSyncAlt size={13} /> Atualizar
                        </button>
                    </div>
                </div>

                <div className="fin-pp__statusbar" style={{ flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                    {FAIXAS.map(f => (
                        <button
                            key={f.valor}
                            type="button"
                            className="fin-pp__statuschip"
                            onClick={() => setFaixa(f.valor)}
                            style={{
                                cursor: 'pointer',
                                background: faixa === f.valor ? 'var(--fin-acw)' : 'transparent',
                                borderColor: faixa === f.valor ? 'var(--fin-acb)' : 'var(--fin-bd)',
                                color: faixa === f.valor ? 'var(--fin-ac)' : 'var(--fin-mt)',
                            }}
                        >
                            <span className="fin-pp__statuschip-num">{contadores[f.valor] ?? 0}</span>
                            <span>{f.rotulo}</span>
                        </button>
                    ))}
                </div>

                <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                    <div className="fin-pp__tblwrap">
                        <table className="fin-pp__tbl" style={{ minWidth: 760, tableLayout: 'auto' }}>
                            <thead>
                                <tr>
                                    <th>Código</th>
                                    <th>Fornecedor</th>
                                    <th>CNPJ</th>
                                    <th>Recebida em</th>
                                    <th>Situação</th>
                                    <th style={{ width: 110 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {carregando ? (
                                    <tr><td colSpan={6} className="fin-pp__empty" style={{ padding: 0 }}><TabelaLoading /></td></tr>
                                ) : submissoes.length === 0 ? (
                                    <tr><td colSpan={6} className="fin-pp__empty">Nenhuma submissão nesta faixa</td></tr>
                                ) : submissoes.map(s => (
                                    <tr key={s.id}>
                                        <td className="fin-pp__cell-muted">{s.codigo ?? '—'}</td>
                                        <td className="fin-pp__cell-strong">
                                            {s.fornecedor
                                                ? (s.nome ?? `(fornecedor ${s.fornecedor})`)
                                                : (s.razaosocial || 'Cadastro novo')}
                                            {s.caminho === 'troca_cnpj' && (
                                                <span style={{ display: 'block', fontSize: 12, color: 'var(--fin-mt)', fontWeight: 400 }}>
                                                    substitui {s.substitui_nome || 'cadastro removido'}{s.substitui_cnpj ? ` · CNPJ ${s.substitui_cnpj}` : ''}
                                                </span>
                                            )}
                                            {s.nomefantasia && s.nomefantasia !== s.nome && (
                                                <span style={{ display: 'block', fontSize: 12, color: 'var(--fin-mt)', fontWeight: 400 }}>
                                                    {s.nomefantasia}
                                                </span>
                                            )}
                                            {s.outro_link && s.link_fornecedor_nome && (
                                                <span style={{ display: 'block', fontSize: 12, color: 'var(--fin-mt)', fontWeight: 400 }}>
                                                    pelo link de {s.link_fornecedor_nome}
                                                </span>
                                            )}
                                        </td>
                                        <td className="fin-pp__cell-muted">{s.cnpj || '—'}</td>
                                        <td className="fin-pp__cell-muted">{dataBr(s.created_at)}</td>
                                        <td>
                                            <span className={'fin-pp__pill ' + (PILL[s.status] || 'is-muted')}>
                                                {ROTULO[s.status] || s.status}
                                            </span>
                                            {/* O caminho que o CNPJ abriu. Atualização é o comum e não ganha selo. */}
                                            {s.caminho && s.caminho !== 'atualizacao' && (
                                                <span className={'fin-pp__minitag ' + (s.caminho === 'troca_cnpj' ? 'is-warn' : 'is-info')} style={{ marginLeft: 6 }}>
                                                    {rotuloDe(CAMINHOS_SUBMISSAO, s.caminho)}
                                                </span>
                                            )}
                                            {s.status === 'rejeitada' && s.motivo_rejeicao && (
                                                <span style={{ display: 'block', fontSize: 12, color: 'var(--fin-mt)', marginTop: 4 }}>
                                                    {s.motivo_rejeicao}
                                                </span>
                                            )}
                                            {/* Seção 11: "Documentação validada por" e "Atualizado por".
                                                O servidor já devolvia `revisado_em` e ninguém exibia —
                                                abrindo uma submissão aplicada para auditar, não dava
                                                para dizer quem aprovou nem quando. */}
                                            {s.revisado_em && (
                                                <span style={{ display: 'block', fontSize: 12, color: 'var(--fin-mt)', marginTop: 4 }}>
                                                    {dataBr(s.revisado_em)}
                                                    {s.revisor ? ` por ${s.revisor}` : ''}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="fin-pp__btn fin-pp__btn--sm"
                                                onClick={() => abrir(s.id)}
                                            >
                                                Revisar
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {detalhe && (
                <div
                    className="modal fade show d-block fin-pp fin-pp__modalbg"
                    onClick={(e) => { if (e.target === e.currentTarget) setDetalhe(null); }}
                >
                    {/* `modal-dialog-scrollable` (padrão da casa) porque o corpo é a
                        comparação campo a campo, e ela cresce com o tanto que o
                        fornecedor declarou. Sem ele, uma submissão completa empurra o
                        rodapé para fora da tela e o revisor perde de vista justamente
                        os botões de aprovar e rejeitar. */}
                    <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
                        <div className="modal-content fin-pp__modal">
                            <div className="fin-pp__modal-head">
                                <span className="fin-pp__modal-head-ic"><FaClipboardCheck size={17} /></span>
                                <h3 className="fin-pp__modal-title">
                                    {!ehNovo
                                        ? 'Revisar cadastro'
                                        : (detalhe.submissao.caminho === 'troca_cnpj' ? 'Revisar troca de CNPJ' : 'Revisar cadastro novo')}
                                    <span className="fin-pp__modal-sub">
                                        {aplicarEm
                                            ? `Aplicar grava no cadastro escolhido, ${aplicarEm.label}; nada é criado nem inativado`
                                            : !ehNovo
                                            ? 'Aplicar grava no cadastro do fornecedor e fica registrado no histórico'
                                            : (detalhe.submissao.caminho === 'troca_cnpj' && substituido
                                                // Rede, preferencial, tipo de serviço e subserviços passam do antigo para o novo.
                                                ? `Aplicar cria o fornecedor com os dados declarados e a classificação interna de ${substituido.nome}`
                                                    + ` (código ${substituido.codigo}, CNPJ ${substituido.cnpj || '—'}), e inativa esse cadastro`
                                                : 'Aplicar cria o fornecedor com os dados declarados')}
                                    </span>
                                    {detalhe.submissao?.outro_link && detalhe.submissao.link_fornecedor_nome && (
                                        <span className="fin-pp__minitag is-info" style={{ marginTop: 4 }}>
                                            pelo link de {detalhe.submissao.link_fornecedor_nome}
                                        </span>
                                    )}
                                </h3>
                                <button type="button" className="fin-pp__modal-close" onClick={() => setDetalhe(null)}>
                                    <FaTimes size={16} />
                                </button>
                            </div>

                            <div className="modal-body">
                                {/* Fora do "Carregando…": trocar o destino recarrega o comparativo, e o
                                    campo continua na tela enquanto isso. */}
                                {detalhe.submissao?.status === 'recebida' && (
                                    <div className="fin-pp__filters" style={{ marginBottom: 12 }}>
                                        <FinField label="Aplicar em" full>
                                            <ReactSelect
                                                placeholder={detalhe.submissao.fornecedor
                                                    ? 'O cadastro deste CNPJ — ou busque outro por nome, código ou CNPJ'
                                                    : 'Cadastro novo — ou busque um existente por nome, código ou CNPJ'}
                                                loadOptions={buscarFornecedores}
                                                value={aplicarEm}
                                                onChange={escolherDestino}
                                                isClearable
                                            />
                                        </FinField>
                                    </div>
                                )}
                                {detalhe.carregando ? (
                                    <div className="fin-pp__empty">Carregando…</div>
                                ) : (
                                    <>
                                        <div className="fin-pp__tblwrap">
                                            <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                                                <thead>
                                                    <tr>
                                                        <th>Campo</th>
                                                        <th>No cadastro</th>
                                                        <th>Declarado</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {comparacao().map(l => (
                                                        <tr key={l.rotulo}>
                                                            <td className="fin-pp__cell-muted">{l.rotulo}</td>
                                                            <td className="fin-pp__cell-muted">{l.atual}</td>
                                                            <td className={l.mudou ? 'fin-pp__cell-strong' : 'fin-pp__cell-muted'}>
                                                                {l.declarado}
                                                                {l.mudou && (
                                                                    <span className="fin-pp__pill is-info" style={{ marginLeft: 8 }}>
                                                                        alterado
                                                                    </span>
                                                                )}
                                                                {l.vazio && (
                                                                    <span className="fin-pp__pill is-warn" style={{ marginLeft: 8 }}>
                                                                        não respondido — mantém o atual
                                                                    </span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Contatos LADO A LADO. A aprovação substitui a lista
                                            inteira, não faz merge — mostrar só o declarado
                                            escondia exatamente o que ia sumir. É o mesmo
                                            raciocínio do "não respondido" dos escalares,
                                            aplicado ao único campo que é sobrescrito em bloco. */}
                                        {contatosAtuais.length + contatosDeclarados.length > 0 && (
                                            <div style={{ marginTop: 14 }}>
                                                <div className="fin-pp__cphead" style={{ marginBottom: 8 }}>
                                                    <span className="fin-pp__cphead-title">Contatos</span>
                                                    {/* Lista VAZIA não remove nada: o aprovar() só grava
                                                        contatos quando o payload traz algum (`!empty`),
                                                        então zero declarados preserva os atuais. Alertar
                                                        "serão removidos" aí faria o revisor rejeitar uma
                                                        submissão boa para proteger contatos que nunca
                                                        estiveram em risco. */}
                                                    {contatosDeclarados.length === 0 && contatosAtuais.length > 0 && (
                                                        <span className="fin-pp__pill is-warn" style={{ marginLeft: 8 }}>
                                                            não respondido — mantém os atuais
                                                        </span>
                                                    )}
                                                    {contatosDeclarados.length > 0 && contatosDeclarados.length < contatosAtuais.length && (
                                                        <span className="fin-pp__pill is-warn" style={{ marginLeft: 8 }}>
                                                            {contatosAtuais.length - contatosDeclarados.length} serão removidos
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="fin-pp__tblwrap">
                                                    <table className="fin-pp__tbl">
                                                        <thead>
                                                            <tr>
                                                                <th>No cadastro</th>
                                                                <th>Declarado</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {Array.from({ length: Math.max(contatosAtuais.length, contatosDeclarados.length) }).map((_, i) => (
                                                                <tr key={i}>
                                                                    <td className="fin-pp__cell-muted">{rotuloContato(contatosAtuais[i])}</td>
                                                                    <td className={contatosDeclarados[i] ? 'fin-pp__cell-strong' : 'fin-pp__cell-muted'}>
                                                                        {rotuloContato(contatosDeclarados[i])}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        {/* Cabeçalho do anexo + a regra da seção 8. Ficam no topo
                                            porque mudam a leitura de tudo abaixo: um "cadastro
                                            inicial" tem a coluna "No cadastro" vazia por natureza,
                                            e um favorecido divergente é motivo de devolução
                                            independente do resto estar certo. */}
                                        {detalhe.submissao?.natureza && (
                                            <div style={{ marginTop: 4, marginBottom: 12, fontSize: 13 }}>
                                                <span style={{ color: 'var(--fin-mt)' }}>Natureza do cadastro: </span>
                                                <strong>{rotuloDe(NATUREZAS_CADASTRO, detalhe.submissao.natureza)}</strong>
                                            </div>
                                        )}

                                        {favorecidoDiverge && (
                                            <div className="fin-pp__banner is-warn" style={{ marginBottom: 12 }}>
                                                <span>
                                                    <strong>CNPJ do favorecido diferente do CNPJ da empresa.</strong>{' '}
                                                    O anexo exige que a conta pertença ao emitente do documento fiscal —
                                                    divergência é devolvida para regularização.
                                                </span>
                                            </div>
                                        )}

                                        {/* ── Seção 1: conferência da Receita ──────────────────
                                            Esta conferência existia no formulário público e saiu de
                                            lá por LGPD — o token não prova quem abriu o link. Ela não
                                            deixou de ser exigida pelo anexo; mudou de lado. Aqui o
                                            acesso é autenticado e restrito aos perfis de revisão, e é
                                            o revisor que confronta a fonte autoritativa com o
                                            declarado. */}
                                        {detalhe.receita?.disponivel && (
                                            <div style={{ marginTop: 18 }}>
                                                <div className="fin-pp__cphead" style={{ marginBottom: 8 }}>
                                                    <span className="fin-pp__cphead-title">Dados da Receita Federal</span>
                                                    <span className="fin-pp__cphead-sub">confira contra o que foi declarado</span>
                                                </div>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, marginBottom: 10 }}>
                                                    {detalhe.receita.porte && (
                                                        <div>
                                                            <span style={{ display: 'block', fontSize: 12, color: 'var(--fin-mt)' }}>Porte</span>
                                                            <strong>{detalhe.receita.porte}</strong>
                                                        </div>
                                                    )}
                                                    {detalhe.receita.data_constituicao && (
                                                        <div>
                                                            <span style={{ display: 'block', fontSize: 12, color: 'var(--fin-mt)' }}>Data de constituição</span>
                                                            <strong>{dataBr(detalhe.receita.data_constituicao)}</strong>
                                                        </div>
                                                    )}
                                                </div>
                                                {detalhe.receita.cnaes?.length > 0 && (
                                                    <div>
                                                        <span style={{ display: 'block', fontSize: 12, color: 'var(--fin-mt)', marginBottom: 6 }}>CNAEs</span>
                                                        {detalhe.receita.cnaes.map(c => (
                                                            <div key={c.codigo} style={{ fontSize: 13, marginBottom: 3 }}>
                                                                <span className={'fin-pp__pill ' + (c.principal ? 'is-info' : 'is-muted')} style={{ marginRight: 8 }}>
                                                                    {c.principal ? 'Principal' : 'Secundário'}
                                                                </span>
                                                                {c.codigo} — {c.descricao}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* ── Seções 4 a 7: declarado × o que está no cadastro ──
                                            Mesma leitura das outras seções: à esquerda o que já
                                            existe, à direita o que ele declarou. Linha sem
                                            declaração é "não mexeu", não "apagou" — é assim que o
                                            aprovar() trata, e a tela precisa dizer o mesmo. */}
                                        {fiscalDeclarado && (
                                            <div style={{ marginTop: 18 }}>
                                                <div className="fin-pp__cphead" style={{ marginBottom: 8 }}>
                                                    <span className="fin-pp__cphead-title">Perfil tributário</span>
                                                    <span className="fin-pp__cphead-sub">seções 4 a 7</span>
                                                </div>
                                                <div className="fin-pp__tblwrap">
                                                    <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                                                        <thead>
                                                            <tr>
                                                                <th>Campo</th>
                                                                <th>No cadastro</th>
                                                                <th>Declarado</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {LINHAS_FISCAIS.map(linha => (
                                                                <tr key={linha.campo}>
                                                                    <td className="fin-pp__cell-muted">{linha.rotulo}</td>
                                                                    <td className="fin-pp__cell-muted">
                                                                        {linha.exibir(fiscalAtual?.[linha.campo]) || '—'}
                                                                    </td>
                                                                    <td className={linha.exibir(fiscalDeclarado[linha.campo]) ? 'fin-pp__cell-strong' : 'fin-pp__cell-muted'}>
                                                                        {linha.exibir(fiscalDeclarado[linha.campo]) || '—'}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>

                                                {/* Retenções em bloco próprio: são oito pares, e
                                                    espalhá-los na tabela acima afogaria o resto. */}
                                                <div style={{ marginTop: 10, fontSize: 13 }}>
                                                    <span style={{ color: 'var(--fin-mt)' }}>Retenções declaradas: </span>
                                                    {retencoesDeclaradas.length > 0
                                                        ? retencoesDeclaradas.map(r => (
                                                            <span
                                                                key={r.valor}
                                                                className="fin-pp__pill is-info"
                                                                style={{ marginRight: 6 }}
                                                            >
                                                                {r.rotulo}{r.aliquota ? ` ${r.aliquota}` : ''}
                                                            </span>
                                                        ))
                                                        : (fiscalDeclarado.sem_retencoes
                                                            /* "Declarou que não há" e "não respondeu" produziam
                                                               a MESMA tela vazia, e era sobre isso que o revisor
                                                               aprovava. Agora sao dois textos diferentes. */
                                                            ? <span className="fin-pp__pill is-ok">Declarou que nenhuma retenção incide</span>
                                                            : <span className="fin-pp__cell-muted">não respondeu esta seção</span>)}
                                                </div>
                                                {/* A observação por tributo, VISÍVEL — não em `title`.
                                                    Ela é o "até quando" que o fornecedor declarou sobre
                                                    a retenção, e aprovar sem ler é o mesmo defeito que
                                                    esta entrega corrigiu no `regime_irpj`. Tooltip não
                                                    conta como ter visto. */}
                                                {retencoesDeclaradas.some(r => r.observacao) && (
                                                    <div style={{ marginTop: 6, fontSize: 13, lineHeight: 1.7 }}>
                                                        {retencoesDeclaradas.filter(r => r.observacao).map(r => (
                                                            <div key={r.valor}>
                                                                <span style={{ color: 'var(--fin-mt)' }}>{r.rotulo}: </span>
                                                                {r.observacao}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {fiscalDeclarado.retencoes_outras && (
                                                    <div style={{ marginTop: 6, fontSize: 13 }}>
                                                        <span style={{ color: 'var(--fin-mt)' }}>Outras: </span>
                                                        {fiscalDeclarado.retencoes_outras}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* ── Seção 9 ──────────────────────────────────────── */}
                                        {(detalhe.documentos || []).length > 0 && (
                                            <div style={{ marginTop: 18 }}>
                                                <div className="fin-pp__cphead" style={{ marginBottom: 8 }}>
                                                    <span className="fin-pp__cphead-title">Documentos anexados</span>
                                                    <span className="fin-pp__cphead-sub">{detalhe.documentos.length} arquivo(s)</span>
                                                </div>
                                                <div className="fin-pp__tblwrap">
                                                    <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                                                        <tbody>
                                                            {detalhe.documentos.map(doc => (
                                                                // `doc.nome` vem do `arquivo`, não do documento. A tela
                                                                // lia `nome_arquivo` e `tamanho`, colunas que saíram de
                                                                // `fornecedor_documento` quando ela foi alinhada ao padrão
                                                                // do `eventoarquivo` — as duas chegavam `undefined`, e o
                                                                // revisor via uma linha sem nome de arquivo nenhum.
                                                                <tr key={doc.id}>
                                                                    <td className="fin-pp__cell-muted">{rotuloDe(TIPOS_DOCUMENTO, doc.tipo)}</td>
                                                                    <td className="fin-pp__cell-strong">{doc.nome || '—'}</td>
                                                                    <td className="fin-pp__cell-muted">{dataBr(doc.created_at)}</td>
                                                                    {/* Aprovar documento que não dá para abrir é aprovar no
                                                                        escuro. Mesma rota que serve todo anexo do sistema. */}
                                                                    <td style={{ width: 60 }}>
                                                                        {doc.arquivo && (
                                                                            <FormButton
                                                                                method="get"
                                                                                small
                                                                                title="Baixar documento"
                                                                                action={process.env.REACT_APP_API_URL + '/arquivo/download/' + doc.arquivo}
                                                                                icon={<FaDownload size={14} />}
                                                                                color="primary"
                                                                                data={{ token: localStorage.getItem('token') }}
                                                                            />
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        {/* Fora do bloco acima de propósito: se nenhum arquivo
                                            chegou, a tabela nem aparece, e é quando isto mais importa.
                                            No caminho de 30 MB os arquivos sobem depois do envio, e
                                            quem fecha a página no meio deixa a submissão sem eles. */}
                                        {(detalhe.anexosFaltando || []).length > 0 && (
                                            <div style={{ marginTop: 12 }}>
                                                <div className="fin-pp__cphead" style={{ marginBottom: 6 }}>
                                                    <span className="fin-pp__cphead-title">Anexos que não chegaram</span>
                                                    <span className="fin-pp__cphead-sub">O fornecedor anexou, mas o envio dos arquivos parou antes de terminar.</span>
                                                </div>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                    {detalhe.anexosFaltando.map(tipo => (
                                                        <span key={tipo} className="fin-pp__pill is-warn">{rotuloDe(TIPOS_DOCUMENTO, tipo)}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* As perguntas que o fornecedor marcou como "não se
                                            aplica". Sem isto o revisor vê campo vazio e não sabe
                                            se ele pulou ou se afirmou que a pergunta não o
                                            alcança — que é a diferença inteira do recurso. */}
                                        {(detalhe.submissao?.payload?.nao_aplica || []).length > 0 && (
                                            <div style={{ marginTop: 18 }}>
                                                <div className="fin-pp__cphead" style={{ marginBottom: 8 }}>
                                                    <span className="fin-pp__cphead-title">Não se aplica</span>
                                                    <span className="fin-pp__cphead-sub">
                                                        {detalhe.submissao.payload.nao_aplica.length} pergunta(s)
                                                    </span>
                                                </div>
                                                <div style={{ fontSize: 13 }}>
                                                    {detalhe.submissao.payload.nao_aplica.map(rotulo => (
                                                        <span key={rotulo} className="fin-pp__pill is-muted" style={{ marginRight: 6, marginBottom: 4, display: 'inline-block' }}>
                                                            {rotulo}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* ── Seção 10: o aceite ───────────────────────────────
                                            Mostra a versão que ELE aceitou, não a de hoje. Se o
                                            texto mudou desde então, o aviso diz isso em vez de
                                            exibir um termo que ele nunca leu. */}
                                        {detalhe.submissao?.aceite_em && (
                                            <div style={{ marginTop: 18 }}>
                                                <div className="fin-pp__cphead" style={{ marginBottom: 8 }}>
                                                    <span className="fin-pp__cphead-title">Aceite eletrônico</span>
                                                    <span className="fin-pp__cphead-sub">versão {detalhe.submissao.aceite_versao}</span>
                                                </div>
                                                <div style={{ fontSize: 13, color: 'var(--fin-mt)', lineHeight: 1.7 }}>
                                                    Aceito em {dataBr(detalhe.submissao.aceite_em)} · IP {detalhe.submissao.aceite_ip || '—'}
                                                    <br />
                                                    <span style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>
                                                        SHA-256 {detalhe.submissao.aceite_hash}
                                                    </span>
                                                </div>
                                                {detalhe.declaracoesVersaoAtual
                                                    && detalhe.submissao.aceite_versao !== detalhe.declaracoesVersaoAtual && (
                                                    <div className="fin-pp__banner is-warn" style={{ marginTop: 10 }}>
                                                        <span>
                                                            O texto das declarações mudou desde este aceite (hoje é a versão{' '}
                                                            {detalhe.declaracoesVersaoAtual}). O que ele aceitou está registrado pelo hash acima.
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* ── Seção 10: quem assinou ───────────────────────────
                                            O aceite acima diz que alguém concordou; isto diz quem
                                            responde pela declaração. */}
                                        {detalhe.submissao?.assinante_nome && (
                                            <div style={{ marginTop: 14, fontSize: 13, lineHeight: 1.7 }}>
                                                <span style={{ color: 'var(--fin-mt)' }}>Assinado por </span>
                                                <strong>{detalhe.submissao.assinante_nome}</strong>
                                                {detalhe.submissao.assinante_cargo ? ` · ${detalhe.submissao.assinante_cargo}` : ''}
                                                {detalhe.submissao.assinante_cpf ? ` · CPF ${detalhe.submissao.assinante_cpf}` : ''}
                                                {(detalhe.submissao.assinatura_local || detalhe.submissao.assinatura_data) && (
                                                    <span style={{ color: 'var(--fin-mt)' }}>
                                                        {` — ${detalhe.submissao.assinatura_local || ''}`}
                                                        {detalhe.submissao.assinatura_data ? `, ${dataBr(detalhe.submissao.assinatura_data)}` : ''}
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {/* ── Seção 11: classificação e ciclo de revisão ───────
                                            Uso interno, nunca sai no formulário público. Mora em
                                            `fornecedor_classificacao_fiscal`, ao lado do regime que
                                            classifica. */}
                                        {detalhe.atual?.fiscal && (
                                            <div style={{ marginTop: 18 }}>
                                                <div className="fin-pp__cphead" style={{ marginBottom: 8 }}>
                                                    <span className="fin-pp__cphead-title">Classificação de crédito</span>
                                                    <span className="fin-pp__cphead-sub">uso interno</span>
                                                </div>

                                                <div style={{ fontSize: 13, color: 'var(--fin-mt)', marginBottom: 10 }}>
                                                    Próxima revisão: <strong>{dataBr(detalhe.atual.fiscal.proxima_revisao)}</strong>
                                                </div>

                                                <div className="fin-pp__filters">
                                                    <FinField label="Crédito IBS/CBS" grow>
                                                        <ReactSelect
                                                            options={opcoesDe(CLASSIFICACOES_CREDITO)}
                                                            placeholder={placeholderDe(CLASSIFICACOES_CREDITO)}
                                                            isClearable
                                                            value={(controleEdit.classificacao_credito ?? detalhe.atual.fiscal.classificacao_credito)
                                                                ? { value: controleEdit.classificacao_credito ?? detalhe.atual.fiscal.classificacao_credito } : null}
                                                            onChange={e => setControleEdit(c => ({ ...c, classificacao_credito: e ? e.value : '' }))}
                                                        />
                                                        {!detalhe.atual.fiscal.classificacao_credito && detalhe.classificacaoSugerida && (
                                                            <span style={{ display: 'block', fontSize: 12, color: 'var(--fin-mt)', marginTop: 4 }}>
                                                                sugerido pelo regime declarado: {rotuloDe(CLASSIFICACOES_CREDITO, detalhe.classificacaoSugerida)}
                                                            </span>
                                                        )}
                                                    </FinField>
                                                    <FinField label="% estimado">
                                                        <FinInput
                                                            placeholder="Ex.: 8,8%"
                                                            value={controleEdit.credito_percentual ?? detalhe.atual.fiscal.credito_percentual ?? ''}
                                                            onChange={e => setControleEdit(c => ({ ...c, credito_percentual: e.target.value }))}
                                                        />
                                                    </FinField>
                                                    <FinField label="Periodicidade da revisão">
                                                        <ReactSelect
                                                            options={opcoesDe(PERIODICIDADES_REVISAO)}
                                                            placeholder={placeholderDe(PERIODICIDADES_REVISAO)}
                                                            isClearable
                                                            value={(controleEdit.periodicidade_meses ?? detalhe.atual.fiscal.periodicidade_meses)
                                                                ? { value: String(controleEdit.periodicidade_meses ?? detalhe.atual.fiscal.periodicidade_meses) } : null}
                                                            onChange={e => setControleEdit(c => ({ ...c, periodicidade_meses: e ? e.value : '' }))}
                                                        />
                                                    </FinField>
                                                </div>

                                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                                                    <button
                                                        type="button"
                                                        className="fin-pp__btn fin-pp__btn--sm"
                                                        disabled={salvandoControle || Object.keys(controleEdit).length === 0}
                                                        onClick={salvarControle}
                                                    >
                                                        {salvandoControle ? 'Salvando…' : 'Salvar classificação'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Retrocompatibilidade: a conferência da Receita saiu do
                                            formulário público (o token não prova quem abriu, então
                                            porte, data de constituição e CNAE eram dado da empresa
                                            exposto a quem tivesse o link). Submissões feitas ANTES
                                            da remoção já carregam o campo, e apagar este bloco
                                            esconderia do revisor uma divergência legitimamente
                                            apontada. Não renderiza nada para as novas. */}
                                        {detalhe.submissao?.payload?.receita_conferida === false && (
                                            <div className="fin-pp__banner is-warn" style={{ marginTop: 14 }}>
                                                <span>
                                                    <strong>O fornecedor apontou divergência na Receita.</strong>{' '}
                                                    {detalhe.submissao.payload.receita_divergencia || 'Sem detalhe informado.'}
                                                </span>
                                            </div>
                                        )}

                                        {/* Fica junto do bloco de ação, não no topo: é a última
                                            coisa que o revisor escreve, depois de ter lido tudo. */}
                                        {detalhe.submissao?.status === 'recebida' && (
                                            <div className="fin-pp__filters" style={{ marginTop: 18 }}>
                                                <FinField label="Observação da revisão" full>
                                                    <FinTextarea
                                                        rows={2}
                                                        placeholder="Opcional — o que você conferiu, ou por que aceitou assim"
                                                        value={observacao}
                                                        onChange={e => setObservacao(e.target.value)}
                                                    />
                                                </FinField>
                                            </div>
                                        )}

                                        {/* Submissões já revisadas: a nota fica visível, que é o
                                            ponto de tê-la registrado. */}
                                        {detalhe.submissao?.observacao && (
                                            <div style={{ marginTop: 14, fontSize: 13, lineHeight: 1.6 }}>
                                                <span style={{ color: 'var(--fin-mt)' }}>Observação da revisão: </span>
                                                {detalhe.submissao.observacao}
                                            </div>
                                        )}

                                        {detalhe.submissao?.status === 'recebida' && (
                                            <div className="fin-pp__filters" style={{ marginTop: 16 }}>
                                                <FinField label="Motivo, se for rejeitar" full>
                                                    <FinTextarea
                                                        rows={2}
                                                        placeholder="Volta para o fornecedor corrigir — seja específico"
                                                        value={motivo}
                                                        onChange={e => setMotivo(e.target.value)}
                                                    />
                                                </FinField>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className="fin-pp__modal-foot">
                                <button type="button" className="fin-pp__btn" onClick={() => setDetalhe(null)}>
                                    Fechar
                                </button>
                                {detalhe.submissao?.status === 'recebida' && (
                                    <>
                                        <button
                                            type="button"
                                            className="fin-pp__btn fin-pp__btn--danger"
                                            disabled={salvando || !motivo.trim()}
                                            onClick={() => revisar('rejeitar')}
                                        >
                                            <FaBan size={12} /> Rejeitar
                                        </button>
                                        <button
                                            type="button"
                                            className="fin-pp__btn fin-pp__btn--ok"
                                            disabled={salvando}
                                            onClick={() => revisar('aprovar')}
                                        >
                                            <FaCheck size={12} /> {ehNovo ? 'Criar fornecedor' : 'Aplicar no cadastro'}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </ErpTemplate>
    );
}
